import React, { useEffect, useState } from 'react';
import RefLink from './RefLink';
import {
  ChevronRight, Download, ExternalLink,
} from 'lucide-react';
import SourceRequestModal, { StatusChip } from './SourceRequestModal';
import { InfoTip } from './FormKit';
import WorkflowRefLink, { workflowSourceLabel } from './WorkflowRefLink';
import EngagementAttachments from './EngagementAttachments';
import FileList, { DownloadLink } from './FileList';
import { ProvenanceBadge, provenanceOf } from './provenance';
import { formatDate, formatDateShort, formatDateTime } from '../../utils/dateFormat';
import { serviceLabel, engagementDisplayName } from '../../utils/engagementName';
import { pieText } from '../../utils/engagementProfile';
import { fetchJSON, API_BASE_URL } from '../../utils/api';
import { fmtBytes, sectionTitle, sectionTitleBar } from '../workflow/constants';
import { DetailGrid, DetailItem } from '../workflow/SectionCard';
import { EngagementNameList } from '../workflow/CoverageDisplay';
import NowName, { StaffNames } from './NowName';
import { qcEstimatedFeeOf, qcNasFeeOf } from '../../pages/qcQuestions';
import { eqrLabel } from '../../pages/qcAddendum';
import { QcFormView } from '../workflow/QcForm';
import { engagementNo } from '../../utils/engagementNo';
import AddendumRow, { sortAddendaNewestFirst } from '../records/AddendumRow';
import { FormPdfDownload } from '../records/legacyImport';

/**
 * Read-only view of ONE engagement — a READING layout, not a disabled edit
 * form (docs/conventions.md §2): sectioned Overview / Team /
 * QC / Attachments / History, with the source application behind its badge.
 *
 * Two surfaces render this exact component, which is the point — a field added
 * here appears on both:
 *   /qm/engagements       the management list's View modal   (engagements:edit)
 *   /my/engagements/:id   My Records, for the team           (login only)
 *
 * They differ only in which API serves the data, so every read is built from
 * `basePath`: '/api/engagements/12' or '/api/my/engagements/12'. Nothing here
 * writes — editing lives in the management page's Edit modal.
 */

const fmtDate = (v) => (v ? formatDate(v) : '—');
const CONSOLIDATED_LABEL = { yes: 'Yes', no: 'No' };

// Day count between sign-off and the archival deadline.
// The deadline the API serves is already "manual override ?? default deadline",
// so the difference is computed — the period's day count for the default, the real gap when a
// reviewer or manager picked another date — never hard-coded.
const signOffPlusDays = (signOff, deadline) => {
  if (!signOff || !deadline) return null;
  const ms = Date.parse(String(deadline).slice(0, 10)) - Date.parse(String(signOff).slice(0, 10));
  if (!Number.isFinite(ms)) return null;
  const days = Math.round(ms / 86400000);
  return `sign-off + ${days} day${days === 1 ? '' : 's'}`;
};

// Summary rows of the Engagement Registration application (shared by the list
// Source badge and the Source section here).
export const ENG_SOURCE_FIELDS = (r) => [
  ['Request', r.requestNo],
  ['Requested By', r.requesterEmail],
  ['Submitted', formatDateTime(r.createdAt)],
  ['Partner in Charge', r.partnerName],
  ['Manager in Charge', r.managerName],
  ['Service Provided', r.nature],
  ['Team Member(s)', <StaffNames list={r.teamMembers} />],
  ['Engagement Side', <StatusChip value={r.engStatus} />],
  ['CaseWare Side', <StatusChip value={r.cwStatus} />],
  ['Charge Code Side', <StatusChip value={r.ccStatus} />],
];

// ── Change history timeline (engagement_events, docs/modules/engagements.md) ──

const FIELD_LABELS = {
  clientGroup: 'Client Group',
  engagementName: 'Engagement Name',
  entityName: 'Entity Name',
  entityId: 'Entity Link',
  yearEnd: 'Year End (FYE)',
  partner: 'Partner',
  director: 'Director',
  manager: 'Manager',
  // Multi-select leads — the diff carries the full readable name list.
  partners: 'Partners',
  directors: 'Directors',
  managers: 'Managers',
  eqr: 'EQR',
  serviceProvided: 'Service Provided',
  status: 'Status',
  isGroupAudit: 'Group Audit',
  underInvestigation: 'Client under investigation by regulatory authorities?',
  tcwgFeeCommunicated: 'Fee Information Communicated to TCWG',
  // Client profile.
  dateOfAppointment: 'Date of Appointment',
  descriptionOfService: 'Description of Service',
  ultimateParent: 'Ultimate Parent',
  overseasOperations: 'Overseas Operations',
  consolidatedFs: 'Consolidated FS',
  isPie: 'Public Interest Entity (PIE)?',
  pieType: 'Type of PIE',
  pieTypeOther: 'Type of PIE (if Others)',
  isTna: 'Transnational Audit (TNA)',
  isTne: 'Transnational Entity (TNE)',
  proposedFees: 'Estimated Fees',
  proposedFeesCcy: 'Estimated Fees Currency',
  proposedFeesShared: 'Estimated Fees Is a Group Total',
  ssicPrimary: 'SSIC Code Primary',
  ssicSecondary: 'SSIC Code Secondary',
  principalActivity1: 'Principal Activity 1',
  principalActivity2: 'Principal Activity 2',
  secRegistered: 'SEC Registered',
  // QM screening question.
  screeningRequired: 'QM Screening',
  expectedFsSigningDate: 'Expected FS Signing Date',
  signOffDate: 'Sign Off Date',
  archived: 'Archived',
  archivalDeadline: 'Archival Deadline',
  archivalDate: 'Archival Date',
  remarks: 'Remarks',
  teamMembers: 'Team Members',
  casewareOpened: 'CaseWare',
  chargeCodeAssigned: 'Charge Code',
  qcType: 'QC Type',
  // The server resolves these diffs to readable labels ("QCR-5 · Entity · FYE
  // dd-mm-yyyy") at read time — never raw FK numbers.
  qcRecordId: 'QC6 / QC7 Form',
  qcRequestId: 'QC6 / QC7 Form',
  // FS / QC35 Form record links — written when a record (or its approval) claims
  // or releases the engagement; the diff carries the record id.
  fsRecordId: 'Linked Signed Financial Statements Record',
  qc35RecordId: 'Linked QC35 Form Record',
};

const ACTION_BADGES = {
  created: 'bg-green-50 text-green-700',
  updated: 'bg-blue-50 text-blue-700',
  deleted: 'bg-red-50 text-red-700',
};

// Who wrote this history row. Unmapped sources fall through and render raw —
// a new writer showing up as `workflow:xyz` is ugly but honest, which beats
// silently labelling it "Manual edit".
// `record:*` = the Management record pages writing the engagement master back
// — worth distinguishing from a plain Engagements-page edit,
// because the change also lives as a formal record with its own history.
// `workflow:*` is resolved by workflowSourceLabel (the flow's ratified display
// name), so a new flow needs no entry here.
const HISTORY_SOURCE_LABELS = {
  // Provenance rule (2026-09-17): Management / Import / Workflow — never "Manual".
  manual: 'Management',
  import: 'Import',
  'record:cwcc': 'CaseWare & Charge Code record',
  'record:fs': 'Signed Financial Statements record',
  'record:qc35': 'QC35 Form record',
  'record:qc': 'QC6 / QC7 record',
};

const historySourceLabel = (source) =>
  HISTORY_SOURCE_LABELS[source] || workflowSourceLabel(source) || source;

const fmtEventValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
};

const fmtEventTime = (v) => { const r = formatDateTime(v); return r || String(v); };

/**
 * Change-history timeline for one engagement (engagement_events). Used by the
 * standalone History modal, the History section inside the View/Edit modal and
 * the My Records detail page, so the full 歷程 reads the same everywhere.
 */
export const EngagementHistory = ({ basePath }) => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`${basePath}/events`);
        if (!cancelled) setEvents(data.data || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [basePath]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {!error && events === null && (
        <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
      )}
      {events !== null && events.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-6">
          No history yet.
        </p>
      )}
      {(events || []).map((ev) => {
        const changed = Object.entries(ev.changes || {});
        return (
          <div key={ev.id} className="border border-slate-200 rounded-lg">
            <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 rounded-t-lg">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${ACTION_BADGES[ev.action] || 'bg-slate-100 text-slate-600'}`}>
                {ev.action}
              </span>
              <span className="text-xs text-slate-500">
                {historySourceLabel(ev.source)}
              </span>
              {/* 促成這一列的申請單——點得過去看那張單。
                  對照表在 WorkflowRefLink，全站共用一份；這裡只是引用。 */}
              {ev.workflowRef && (
                <span className="text-xs text-slate-400">
                  · <WorkflowRefLink refNo={ev.workflowRef} />
                </span>
              )}
              <span className="text-xs text-slate-500">· {ev.actorEmail || 'system'}</span>
              <span className="text-xs text-slate-500 ml-auto whitespace-nowrap">{fmtEventTime(ev.createdAt)}</span>
            </div>
            {changed.length > 0 && (
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500">
                    <th className="px-4 py-1.5 font-medium w-1/3">Field</th>
                    <th className="px-4 py-1.5 font-medium w-1/3">Before</th>
                    <th className="px-4 py-1.5 font-medium w-1/3">After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {changed.map(([field, diff]) => (
                    <tr key={field}>
                      <td className="px-4 py-1.5 text-slate-700 align-top break-words">{FIELD_LABELS[field] || field}</td>
                      <td className="px-4 py-1.5 text-slate-500 align-top break-words">{fmtEventValue(diff.before)}</td>
                      <td className="px-4 py-1.5 text-slate-900 align-top break-words">{fmtEventValue(diff.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Linked QC6/QC7 acceptance ──────────────────────────────────────────────

const QC_FILE_LABEL = { QC_FORM: 'QC document', QC_PREV: 'Previous year QC' };

/**
 * The QC6/QC7 acceptance linked to this engagement. If the QC was submitted as
 * an uploaded document its file(s) are listed with download; if it was the
 * acceptance form, the answers show in a collapsible block.
 * Read-only either way.
 */
export const LinkedQcSection = ({ basePath, preloaded = null }) => {
  // `preloaded` lets a parent that already fetched `${basePath}/qc` (the
  // engagement view reads the fee off it too) hand the result down instead of
  // fetching twice; standalone callers leave it off and this fetches itself.
  const [fetched, setFetched] = useState({ qc: undefined, error: null }); // qc: undefined = loading, null = none linked
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (preloaded) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`${basePath}/qc`);
        if (!cancelled) setFetched({ qc: data.qc || null, error: null });
      } catch (e) { if (!cancelled) setFetched({ qc: undefined, error: e.message }); }
    })();
    return () => { cancelled = true; };
  }, [basePath, preloaded]);

  const { qc, error } = preloaded || fetched;

  if (qc === null) return null; // nothing linked — show nothing
  if (error) {
    return (
      <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
        {error}
      </div>
    );
  }

  const isForm = qc && qc.submissionMethod === 'form';
  const qcFileRow = (f) => ({
    name: f.originalName,
    stacked: true,
    badges: [[QC_FILE_LABEL[f.docType] || 'Supporting', 'bg-slate-100 text-slate-600']],
    meta: [f.sizeBytes ? fmtBytes(f.sizeBytes) : null],
    actions: <DownloadLink href={`${API_BASE_URL}${basePath}/qc/files/${f.id}`} label={null} newTab />,
  });

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <span className="text-sm font-medium text-slate-700">QC6 / QC7 Form</span>
        {/* QC 申請單的詳情路由是 `/workflow/qc/:id`——沒有 qcType 那一層。
            連 `/workflow/qc/qc6/12` 會落到不存在的路徑。
            `canOpenRequest` 由後端算（申請人／點名的審核人／涵蓋案 Partner）——
            看得到記錄不代表開得了來源單，開不了就不給按。 */}
        {qc && qc.sourceRequestId && qc.canOpenRequest && (
          <RefLink to={`/workflow/qc/${qc.sourceRequestId}`} className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-3 h-3" /> Open Request
          </RefLink>
        )}
      </div>
      <div className="p-4 space-y-3">
        {qc === undefined && <p className="text-sm text-slate-500 py-1">Loading…</p>}
        {qc && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">{qc.qcType}</span>
              {qc.sourceRequestNo && <span className="text-slate-700">{qc.sourceRequestNo}</span>}
              <span className="text-xs text-slate-500">· FYE {formatDateShort(qc.yearEnd)}</span>
              <span className="text-xs text-slate-500">· {isForm ? 'QC6 / QC7 form' : 'Uploaded QC document'}</span>
              <ProvenanceBadge source={qc.source} className="ml-1.5" />
            </div>

            {/* The record's CURRENT risk rating — whatever the latest
                addendum set it to — sits beside the reference line. */}
            {qc.risk && (
              <div className="text-sm text-slate-700">
                Current Risk Rating: <span className="font-semibold text-slate-900">{qc.risk.rating || '—'}</span>
                {qc.risk.revisedBy && <span className="text-xs text-slate-500"> — revised by {qc.risk.revisedBy}; originally {qc.risk.original || '—'}</span>}
              </div>
            )}
            {/* The record's CURRENT engagement quality reviewer — an addendum
                may have reassigned it; the Team block above shows the same
                value as the engagement holds it. */}
            {qc.eqr && (
              <div className="text-sm text-slate-700">
                Current Engagement Quality Reviewer: <span className="font-semibold text-slate-900">
                  <NowName value={eqrLabel(qc.eqr.eqr)} now={qc.eqr.eqrNow} />
                </span>
                {qc.eqr.revisedBy && <span className="text-xs text-slate-500"> — set by {qc.eqr.revisedBy}; the form named {eqrLabel(qc.eqr.original)}</span>}
              </div>
            )}

            {isForm ? (
              <div>
                <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg bg-white">
                  <button type="button" onClick={() => setShowForm((v) => !v)} className="flex-1 min-w-0 flex items-center gap-2 text-left">
                    <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${showForm ? 'rotate-90' : ''}`} />
                    <span className="text-sm font-medium text-slate-900">Form {qc.qcType}</span>
                    {qc.approvedOn && <span className="text-xs text-slate-500">· approved {fmtDate(qc.approvedOn)}</span>}
                  </button>
                  {/* Through the engagement's own route — no Management key needed. */}
                  <FormPdfDownload
                    record={qc}
                    formName={qc.qcType}
                    href={`${API_BASE_URL}${basePath}/qc/pdf`}
                    signedHref={(f) => `${API_BASE_URL}${basePath}/qc/files/${f.id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 shrink-0"
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </FormPdfDownload>
                </div>
                {/* The same sectioned tables the QC request detail shows
                    (QcFormView) — the covered engagements feed the header. */}
                {showForm && (
                  <div className="mt-3">
                    <QcFormView qcType={qc.qcType} answers={qc.answers} engagements={qc.engagements || []} />
                  </div>
                )}
                {(qc.files || []).length > 0 && (
                  <FileList files={qc.files} className="mt-2" row={qcFileRow} />
                )}
              </div>
            ) : (
              (qc.files || []).length === 0 ? (
                <p className="text-sm text-slate-500">No QC file attached.</p>
              ) : (
                <FileList files={qc.files} row={qcFileRow} />
              )
            )}

            {/* Addenda filed after approval — read and downloaded HERE, from
                the engagement, the same way the acceptance form is. */}
            {(qc.addenda || []).length > 0 && (
              <div className="space-y-2 pt-1">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Addenda ({qc.addenda.length})</div>
                {sortAddendaNewestFirst(qc.addenda).map((a, i) => (
                  <AddendumRow
                    key={a.id}
                    addendum={a}
                    record={qc}
                    latest={i === 0}
                    pdfHref={`${API_BASE_URL}${basePath}/qc/addenda/${a.id}/pdf`}
                    fileHref={(f) => `${API_BASE_URL}${basePath}/qc/addenda/${a.id}/files/${f.id}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * 這案連結的 Signed Financial Statements 與 QC35 正式記錄。
 *
 * Signed Financial Statements 跟 qc6/7 一樣是「引用到 Engagement 內」——所以這裡跟
 * `LinkedQcSection` 是同一個道理：讀的是**記錄**，改記錄就
 * 改到這裡；也因此「Management 手動建的記錄」在 engagement 上看得見
 * （若 JOIN 的是申請單，手動記錄會整個隱形）。
 *
 * 一份記錄可以被同集團的子公司共用，所以涵蓋超過一案時把名單列出來——不然
 * 「我改了這筆，還有誰會跟著變」在畫面上是沒有答案的。
 */
export const LinkedRecordsSection = ({ basePath, engagementId = null }) => {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchJSON(`${basePath}/records`);
        if (!cancelled) setData({ fs: res.fs || null, qc35: res.qc35 || null });
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [basePath]);

  if (error) {
    return (
      <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
        {error}
      </div>
    );
  }
  if (data === undefined) return null;
  if (!data.fs && !data.qc35) return null;

  const card = (kind, rec, title, requestPath) => {
    if (!rec) return null;
    // 「還有誰跟著這筆記錄走」——只列**別人**，本案自己不必在名單裡。
    const others = (rec.coveredEngagements || [])
      .filter((c) => c.engagementId && c.engagementId !== engagementId);
    const current =(rec.files || []).filter((f) => !f.superseded);
    return (
      <div className="border border-slate-200 rounded-lg">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-lg">
          <span className="text-sm font-medium text-slate-700">{title}</span>
          <RefLink to={`/qm/records/${kind}/${rec.id}`} className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <ExternalLink className="w-3 h-3" /> Open Record
          </RefLink>
          {/* 同 QC 卡：後端說開得了才顯示，不讓人點進 403。 */}
          {rec.sourceRequestId && rec.canOpenRequest && (
            <RefLink to={`${requestPath}/${rec.sourceRequestId}`} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
              <ExternalLink className="w-3 h-3" /> Open Request
            </RefLink>
          )}
        </div>
        <div className="p-4 space-y-3">
          {/* The record's one defining date: FS = signing date, QC35 = archival
              date (the case's archived-on). Both print the same way. */}
          {(rec.sourceRequestNo || (kind === 'fs' && rec.signingDate) || (kind === 'qc35' && rec.archivalDate)) && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {rec.sourceRequestNo && <span className="text-slate-700">{rec.sourceRequestNo}</span>}
              {kind === 'fs' && rec.signingDate && (
                <span className="text-xs text-slate-500">{rec.sourceRequestNo ? '· ' : ''}Signed {fmtDate(rec.signingDate)}</span>
              )}
              {kind === 'qc35' && rec.archivalDate && (
                <span className="text-xs text-slate-500">{rec.sourceRequestNo ? '· ' : ''}Archived {fmtDate(rec.archivalDate)}</span>
              )}
            </div>
          )}
          {others.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-1">
                Shared with {others.length} other engagement{others.length === 1 ? '' : 's'} of this group — editing this record changes {others.length === 1 ? 'it' : 'them'} too.
              </p>
              <EngagementNameList items={others} keyOf={(c) => c.engagementId} />
            </div>
          )}
          {current.length === 0 ? (
            <p className="text-sm text-slate-500">No document attached.</p>
          ) : (
            <FileList
              files={current}
              row={(f) => ({
                name: f.originalName,
                stacked: true,
                meta: [f.sizeBytes ? fmtBytes(f.sizeBytes) : null],
                actions: (
                  <DownloadLink
                    href={`${API_BASE_URL}${basePath}/attachments/${kind}/${f.id}`}
                    label={null}
                    newTab
                  />
                ),
              })}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {card('fs', data.fs, 'Signed Financial Statements', '/workflow/fs')}
      {card('qc35', data.qc35, 'QC35 Form', '/workflow/qc35')}
    </>
  );
};

// ── Attachments (read-only) ────────────────────────────────────────────────
// The presentation is EngagementAttachments — the same panel the workflow
// detail pages use — so a document label only ever needs changing in one file.
// Uploading / superseding stays in the management page's editor.

// `expectDocType` names a document this engagement is supposed to hold — the QM
// screening one when the answer is Yes. Saying "Yes" in
// Management does not itself produce a file, and nothing else on the page would
// ever point that out, so the panel that DOES know what is attached says it.
// Deliberately a note and not a block: a legitimate answer can precede the
// document by days, and this surface edits live records, not drafts.
const ReadOnlyAttachments = ({ basePath, expectDocType = null, expectLabel = '' }) => {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`${basePath}/attachments`);
        if (!cancelled) setFiles(data.data || []);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [basePath]);

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <span className="text-sm font-medium text-slate-700">Attachments</span>
      </div>
      <div className="p-4">
        {error && (
          <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
            {error}
          </div>
        )}
        {!error && files === null && <p className="text-sm text-slate-500 py-1">Loading…</p>}
        {files !== null && (
          <EngagementAttachments
            attachments={files}
            downloadHref={(f) => `${API_BASE_URL}${basePath}/attachments/${f.source}/${f.fileId}`}
          />
        )}
        {files !== null && expectDocType
          && !files.some((f) => f.docType === expectDocType && !f.superseded) && (
          <p className="text-sm text-amber-700 mt-2">No {expectLabel || expectDocType} document attached yet.</p>
        )}
      </div>
    </div>
  );
};

// ── Overview sections (shared builder) ─────────────────────────────────────
// The Overview reads in sections — a plain-uppercase heading over each
// DetailGrid (the badge labels and the section headings are two different
// shapes, so they never compete). One card, six passes:
// what the case is → who runs it → who the client is → the risk flags →
// the setup switches → the lifecycle dates. Remarks spans the tail.
//
// Built by a function (not inline) because the Client Hub renders the same
// sections for a company's engagements — minus the ones it must not show
// (`omit`). A field added here reaches every surface.
const tri = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');
const vd = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);
// Leads are multi-select — read the full lists and fall back
// to the mirrored single column for rows written before that.
// Each name is the snapshot the engagement was written with, so a lead who has
// been renamed since gets "(now: …)" after their name (docs/data-model.md §2).
const leadNames = (list, single, singleNow) => ((list || []).some((m) => m && m.staffName)
  ? <StaffNames list={list} />
  : <NowName value={single} now={singleNow} />);

export const SECTION_TIMELINE = 'Timeline & Archival';
export const SECTION_CWCC = 'CaseWare & Charge Code';

/**
 * The Estimated Fees value: the approved QC6/QC7 form's fee figure when it has
 * one (`qcFee` = qcEstimatedFeeOf(answers, qcType, { engagementId, covered })),
 * else the amount entered at engagement setup. A form covering several
 * engagements shows this engagement's own amount when the form gives one;
 * otherwise its Total, marked "Group total" with the covered engagements in
 * the tip. Management, My Records, the Client Hub and EAR all render this.
 */
export const EstimatedFeeCell = ({ engagement: e, qcFee, qcType, qcSourceRequestNo }) => {
  if (qcFee) {
    const source = `From the approved ${qcType} form${qcSourceRequestNo ? ` (${qcSourceRequestNo})` : ''} — its fee figure supersedes the amount entered at engagement setup. Corrections go through QC Amendment.`;
    const group = qcFee.scope === 'group';
    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        {qcFee.currency} {qcFee.amount.toLocaleString()}
        {group && <span className="text-xs text-slate-500">Group total ({qcFee.coveredCount} engagements)</span>}
        <InfoTip text={group ? (
          <>
            <span className="block">The form gives only a total for all the engagements it covers:</span>
            <span className="block mt-1 whitespace-pre-line">{(qcFee.coveredNames || []).join('\n')}</span>
            <span className="block mt-1.5 text-slate-300">{source}</span>
          </>
        ) : qcFee.scope === 'engagement' ? `This engagement's own amount on the form. ${source}` : source} />
      </span>
    );
  }
  // A Total shared by the engagements one multi-company registration or one
  // Management → New Engagement submission created: shown as it is, marked as
  // a group total (the tip lists them).
  const group = e.proposedFeesShared && e.proposedFees != null ? (e.proposedFeesGroup || []) : null;
  const setupTip = 'Entered at engagement setup — once a QC6 / QC7 form is approved, its fee figure shows here instead.';
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {e.proposedFees != null ? `${e.proposedFeesCcy || 'SGD'} ${e.proposedFees.toLocaleString()}` : '—'}
      {group && <span className="text-xs text-slate-500">Group total ({group.length} engagements)</span>}
      <InfoTip text={group ? (
        <>
          <span className="block">Only a total was entered for these engagements:</span>
          <span className="block mt-1 whitespace-pre-line">{group.map((g) => engagementDisplayName(g.engagementName, g.serviceDescription)).join('\n')}</span>
          <span className="block mt-1.5 text-slate-300">{setupTip}</span>
        </>
      ) : setupTip} />
    </span>
  );
};

/**
 * The Non-audit Services (NAS) Fee value — only the approved QC6/QC7 form
 * carries it (`nasFee` = qcNasFeeOf(...)); the engagement has no field of its own.
 * A form without a single figure shows "See QC form" with its wording in the tip.
 */
export const NasFeeCell = ({ nasFee, qcType, qcSourceRequestNo }) => {
  if (!nasFee) return '—';
  const source = `From the approved ${qcType} form${qcSourceRequestNo ? ` (${qcSourceRequestNo})` : ''}. Corrections go through QC Amendment.`;
  if (nasFee.text) {
    return (
      <span className="inline-flex items-center gap-1">
        See QC form
        <InfoTip text={<><span className="block whitespace-pre-line">{nasFee.text}</span><span className="block mt-1.5 text-slate-300">{source}</span></>} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      {nasFee.none ? 'No NAS' : `${nasFee.currency} ${nasFee.amount.toLocaleString()}`}
      <InfoTip text={source} />
    </span>
  );
};

/**
 * @param e engagement object (management / my / client-hub envelope)
 * @param opts.estimatedFeeCell node for the Estimated Fees row; null = drop the row
 * @param opts.nasFeeCell node for the Non-audit Services (NAS) Fee row; null = drop the row
 * @param opts.sourceCell node for the Source row; null = drop the row
 * @param opts.omit section titles to leave out (e.g. [SECTION_TIMELINE])
 */
export const buildEngagementSections = (e, { estimatedFeeCell = null, nasFeeCell = null, sourceCell = null, omit = [] } = {}) => {
  const sections = [
    ['Engagement', [
      // The engagement's own file reference — what the Independence
      // Confirmation prints in its "File ref." box.
      ['File Ref.', engagementNo(e.id) || '—'],
      // Snapshots as written; "(now: …)" when the client master moved on.
      ['Client Group', <NowName value={e.clientGroup} now={e.clientGroupNow} />],
      ['Entity', e.entityName
        ? <NowName
            value={`${e.entityName}${e.uen ? ` (${e.uen})` : ''}`}
            now={(e.entityNameNow || e.uenNow) ? `${e.entityNameNow || e.entityName} (${e.uenNow || e.uen})` : null}
          />
        : '—'],
      ['Year End (FYE)', fmtDate(e.yearEnd)],
      ['Status', vd(e.status)],
      ['Service Provided', vd(serviceLabel(e.serviceProvided, e.engagementSeq))],
      ['Description of Service', vd(e.descriptionOfService)],
      // From the client master (Clients & Entities); only a value that falls
      // back to the engagement's own record is annotated.
      ['Date of Appointment', e.dateOfAppointment
        ? <span>{fmtDate(e.dateOfAppointment)}{e.dateOfAppointmentSource === 'engagement' && <span className="ml-1.5 text-xs text-slate-500">(recorded on this engagement)</span>}</span>
        : '—'],
      ...(estimatedFeeCell ? [['Estimated Fees', estimatedFeeCell]] : []),
      ...(nasFeeCell ? [['Non-audit Services (NAS) Fee', nasFeeCell]] : []),
      ...(sourceCell ? [['Source', sourceCell]] : []),
    ]],
    ['Team', [
      ['Partner in Charge', leadNames(e.partners, e.partner, e.partnerNow)],
      ['Director(s)', leadNames(e.directors, e.director, e.directorNow)],
      ['Manager(s)', leadNames(e.managers, e.manager, e.managerNow)],
      ['EQR (From the QC6 / QC7 Form or Its Addendum)', <NowName value={e.eqr} now={e.eqrNow} />],
    ]],
    // Client profile — the answers Engagement Registration
    // asks; editable in Management and through Engagement Amendment.
    ['Client Profile', [
      // Written at creation, then by the QC6 / QC7 approval (read-only on
      // Management / EAR from then on, like the EQR).
      [e.qcRecordId ? 'Public Interest Entity (PIE) (Set by QC6 / QC7)' : 'Public Interest Entity (PIE)', vd(pieText(e))],
      ['Ultimate Parent', vd(e.ultimateParent)],
      ['SSIC Code Primary', vd(e.ssicPrimary)],
      ['SSIC Code Secondary', vd(e.ssicSecondary)],
      ['Principal Activity 1', vd(e.principalActivity1)],
      ['Principal Activity 2', vd(e.principalActivity2)],
      ['Overseas Operations', tri(e.overseasOperations)],
      ['Consolidated FS', CONSOLIDATED_LABEL[e.consolidatedFs] || '—'],
      ['SEC Registered', tri(e.secRegistered)],
    ]],
    ['Risk & QM Flags', [
      ['Group Audit', tri(e.isGroupAudit)],
      ['Under Investigation', tri(e.underInvestigation)],
      ['Fee Information Communicated to TCWG', tri(e.tcwgFeeCommunicated)],
      ['QM Screening', tri(e.screeningRequired)],
      ['Transnational Audit (TNA) (Set by QC6 / QC7)', tri(e.isTna)],
      ['Transnational Entity (TNE) (Set by QC6 / QC7)', tri(e.isTne)],
      ['Listed Company (Set by QC6 / QC7)', tri(e.isListed)],
    ]],
    // 兩欄只記「有／沒有」，用語統一 Activated／Not activated——
    // 不因 CaseWare 是「開檔」、Charge Code 是「配碼」而各說各話。
    [SECTION_CWCC, [
      ['CaseWare', e.casewareOpened ? 'Activated' : 'Not activated'],
      ['Charge Code', e.chargeCodeAssigned ? 'Activated' : 'Not activated'],
    ]],
    [SECTION_TIMELINE, [
      // FS 簽署前的提醒日期——QC6/QC7 Conclusion 蓋上來，改走 QC Amendment。
      ['Expected FS Signing Date (Set by QC6 / QC7)', fmtDate(e.expectedFsSigningDate)],
      ['Sign Off Date', fmtDate(e.signOffDate)],
      ['Archival Deadline', (() => {
        const gap = signOffPlusDays(e.signOffDate, e.archivalDeadline);
        return gap ? `${fmtDate(e.archivalDeadline)} (${gap})` : fmtDate(e.archivalDeadline);
      })()],
      ['Archival Date', fmtDate(e.archivalDate)],
    ]],
  ];
  return sections.filter(([title]) => !omit.includes(title));
};

/** Renders buildEngagementSections() output — headings, grids, the Team chips. */
export const EngagementSectionsView = ({ sections, teamMembers = [] }) => (
  <>
    {sections.map(([title, rows], i) => (
      <div key={title} className={i === 0 ? undefined : 'border-t border-slate-200 pt-5 mt-1'}>
        <h4 className={`${sectionTitle} mb-3`}>
          <span className={sectionTitleBar} aria-hidden="true" />
          {title}
        </h4>
        <DetailGrid>
          {rows.map(([label, value]) => (
            <DetailItem key={label} label={label}>{value}</DetailItem>
          ))}
        </DetailGrid>
        {/* The team-member chips live with the leads — one Team section. */}
        {title === 'Team' && (
          <div className="mt-4">
            <DetailItem label="Team Members">
              {(teamMembers || []).length === 0 ? (
                <span className="text-sm font-normal text-slate-500">No team members recorded.</span>
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {teamMembers.map((m) => (
                    <span key={m.staffId ?? m.staffName} className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                      <NowName value={m.staffName} now={m.staffNameNow} />
                    </span>
                  ))}
                </span>
              )}
            </DetailItem>
          </div>
        )}
      </div>
    ))}
  </>
);

// ── The view itself ────────────────────────────────────────────────────────

const EngagementView = ({ engagement, basePath }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const e = engagement;

  // The linked QC record is fetched HERE (not inside LinkedQcSection) because
  // the QC section below and the fee rows read it — once a QC6/QC7 is
  // approved, its fee figure (with its currency) supersedes the amount entered
  // at engagement setup, and its NAS fee fills the NAS row.
  const [qcState, setQcState] = useState({ qc: undefined, error: null });
  useEffect(() => {
    if (!e.qcRecordId) { setQcState({ qc: null, error: null }); return undefined; }
    let cancelled = false;
    setQcState({ qc: undefined, error: null });
    (async () => {
      try {
        const data = await fetchJSON(`${basePath}/qc`);
        if (!cancelled) setQcState({ qc: data.qc || null, error: null });
      } catch (err) { if (!cancelled) setQcState({ qc: undefined, error: err.message }); }
    })();
    return () => { cancelled = true; };
  }, [basePath, e.qcRecordId]);

  const estimatedFeeCell = (e.qcRecordId && qcState.qc === undefined && !qcState.error)
    ? '…' // QC record still loading
    : (
      <EstimatedFeeCell
        engagement={e}
        qcFee={qcState.qc ? qcEstimatedFeeOf(qcState.qc.answers, qcState.qc.qcType, { engagementId: e.id, covered: qcState.qc.engagements || [] }) : null}
        qcType={qcState.qc && qcState.qc.qcType}
        qcSourceRequestNo={qcState.qc && qcState.qc.sourceRequestNo}
      />
    );

  const sourceCell = (
    <span>
      {provenanceOf(e.source) === 'workflow' ? (
        <button
          type="button"
          onClick={() => setShowSource(true)}
          title="View the application that created this engagement"
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
        >
          {e.sourceRequestNo || 'Workflow'}
        </button>
      ) : <ProvenanceBadge source={e.source} />}
    </span>
  );

  const nasFeeCell = (e.qcRecordId && qcState.qc === undefined && !qcState.error)
    ? '…'
    : (
      <NasFeeCell
        nasFee={qcState.qc ? qcNasFeeOf(qcState.qc.answers, qcState.qc.qcType) : null}
        qcType={qcState.qc && qcState.qc.qcType}
        qcSourceRequestNo={qcState.qc && qcState.qc.sourceRequestNo}
      />
    );

  const sections = buildEngagementSections(e, { estimatedFeeCell, nasFeeCell, sourceCell });

  return (
    <>
      {/* Overview — sectioned (see buildEngagementSections) */}
      <EngagementSectionsView sections={sections} teamMembers={e.teamMembers} />

      {/* Remarks — full width, after every section */}
      <div className="border-t border-slate-100 pt-3">
        <DetailItem label="Remarks">{vd(e.remarks)}</DetailItem>
      </div>

      {/* QC6/QC7 record — rendered from the fetch made above. */}
      {e.qcRecordId && <LinkedQcSection basePath={basePath} preloaded={qcState} />}

      {/* Signed Financial Statements / QC35 records — 同樣是引用（見 LinkedRecordsSection）。 */}
      <LinkedRecordsSection basePath={basePath} engagementId={e.id} />

      {/* Attachments (read-only) */}
      <ReadOnlyAttachments
        basePath={basePath}
        expectDocType={e.screeningRequired === true ? 'SCREENING' : null}
        expectLabel="screening"
      />

      {/* History (collapsed by default) */}
      <div className="border border-slate-200 rounded-lg">
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50 rounded-lg text-left"
        >
          <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          <span className="text-sm font-medium text-slate-700">History</span>
        </button>
        {showHistory && (
          <div className="p-4 border-t border-slate-200">
            <EngagementHistory basePath={basePath} />
          </div>
        )}
      </div>

      {showSource && (
        <SourceRequestModal
          title={`Source Application · ${e.sourceRequestNo || 'Workflow'}`}
          fetchPath={`${basePath}/source-request`}
          fields={ENG_SOURCE_FIELDS}
          onClose={() => setShowSource(false)}
        />
      )}
    </>
  );
};

export default EngagementView;
